import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PartyPopper, Check, Loader2, Sparkles, CalendarCheck, AlertCircle } from "lucide-react";
import { DunkinAddons } from "../components/DunkinAddons";

export default function FinalPartyDetails() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const [count, setCount] = useState("");
  const [addons, setAddons] = useState([]);
  const [dunkin, setDunkin] = useState([]);
  const [themeNotes, setThemeNotes] = useState("");
  const [cakeNotes, setCakeNotes] = useState("");
  const [bannerNotes, setBannerNotes] = useState("");
  const [allergies, setAllergies] = useState("");
  const [special, setSpecial] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    api.get(`/parties/final-details/${id}`)
      .then(({ data }) => {
        setData(data);
        const fd = data.final_details || {};
        setCount(fd.final_guest_count || data.num_guests || "");
        setAddons((fd.addons || []).map((a) => a.key));
        setDunkin(data.selected_dunkin || []);
        setThemeNotes(fd.theme_notes || data.theme_colors || "");
        setCakeNotes(fd.cake_notes || "");
        setBannerNotes(fd.banner_notes || "");
        setAllergies(fd.allergies || "");
        setSpecial(fd.special_requests || "");
        if (data.submitted) setDone(true);
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "This party could not be found."))
      .finally(() => setLoading(false));
  }, [id]);

  const toggle = (key) => setAddons((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const submit = async () => {
    if (!count || Number(count) < 1) { toast.error("Please enter your final decorating guest count"); return; }
    if (!confirmed) { toast.error("Please confirm that all details are correct"); return; }
    setBusy(true);
    try {
      await api.post(`/parties/final-details/${id}`, {
        final_guest_count: Number(count),
        addon_keys: addons,
        dunkin_addons: dunkin,
        theme_notes: themeNotes, cake_notes: cakeNotes, banner_notes: bannerNotes,
        allergies, special_requests: special, confirmed: true,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen"><Loader2 className="mx-auto animate-spin text-[#E0218A]" size={40} /></div>;
  if (error) return (
    <div data-testid="final-details-error" className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen px-6">
      <h1 className="font-serif font-black text-4xl text-[#121212]">Hmm, something's off</h1>
      <p className="mt-4 text-[#121212]/70">{error}</p>
    </div>
  );
  if (done) return (
    <div data-testid="final-details-done" className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen px-6">
      <PartyPopper className="mx-auto text-[#E0218A]" size={52} />
      <h1 className="font-serif font-black text-5xl text-[#121212] mt-6">Thank you!</h1>
      <p className="mt-4 text-[#121212]/80 text-lg max-w-xl mx-auto">We've received your final party details. We'll calculate your final balance and email you a secure payment link shortly. Can't wait to celebrate with you!</p>
    </div>
  );

  const num = Number(count) || 0;
  const isSelected = (k) => addons.includes(k);
  const favorTotal = num * 12;

  return (
    <div data-testid="final-details-page" className="fade-in bg-[#F2E9D8] min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-6 sm:px-10">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#E0218A]" />
          <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#E0218A]">Final party details</span>
        </div>
        <h1 className="font-serif font-black text-[#121212] leading-[0.95] text-5xl sm:text-6xl">Let's finalize the fun, {data.name?.split(" ")[0] || "friend"}!</h1>

        {/* current details */}
        <div data-testid="fd-summary" className="mt-8 bg-white border-2 border-[#121212] p-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60 inline-flex items-center gap-2"><CalendarCheck size={15} className="text-[#C6FF33]" /> Your party</p>
          <p className="font-serif font-black text-2xl text-[#121212] mt-2">{data.package || "Your party"}</p>
          {(data.desired_date || data.preferred_time) && <p className="text-[#121212]/80 mt-1">{[data.desired_date, data.preferred_time].filter(Boolean).join(" · ")}</p>}
          {(data.child_name || data.child_age) && <p className="text-[#121212]/70 text-sm mt-1">Birthday star: {data.child_name || "—"}{data.child_age ? ` (turning ${data.child_age})` : ""}</p>}
          {data.num_guests && <p className="text-[#121212]/60 text-sm mt-1">Current estimate: {data.num_guests} decorating guests</p>}
          {data.theme_colors && <p className="text-[#121212]/60 text-sm mt-1">Theme: {data.theme_colors}</p>}
        </div>

        {/* final guest count */}
        <div className="mt-8">
          <label className="font-serif font-black text-2xl text-[#121212] block">Final decorating guest count</label>
          <input data-testid="fd-guest-count" type="number" min="1" value={count} onChange={(e) => setCount(e.target.value)} className="mt-3 w-40 px-4 py-3 border-2 border-[#121212] bg-white text-lg font-bold focus:outline-none" />
          <div className="mt-3 flex gap-2 text-sm text-[#121212]/75 bg-white border-l-4 border-[#E0218A] p-3">
            <AlertCircle size={18} className="text-[#E0218A] shrink-0 mt-0.5" />
            <p>Your submitted guest count becomes the guaranteed minimum number of decorating seats you will be charged for. Additional guests may be added later only if space and supplies allow.</p>
          </div>
        </div>

        {/* add-ons */}
        <div className="mt-8">
          <h2 className="font-serif font-black text-3xl text-[#121212] flex items-center gap-3"><Sparkles size={24} className="text-[#E0218A]" fill="#E0218A" /> Optional upgrades</h2>
          <p className="mt-3 text-[#121212]/75 leading-relaxed">Choose any upgrades you'd like. These are added to your final party balance.</p>
          <div className="mt-5 space-y-4" data-testid="fd-addons">
            {(data.addons_catalog || []).map((a) => {
              const checked = isSelected(a.key);
              const perGuest = a.unit === "per_guest";
              return (
                <label key={a.key} data-testid={`fd-addon-${a.key}`} className={`flex gap-4 p-5 border-2 cursor-pointer transition-all ${checked ? "border-[#E0218A] bg-[#E0218A]/5" : "border-[#121212] bg-white"}`}>
                  <span className={`shrink-0 mt-0.5 w-6 h-6 border-2 border-[#121212] flex items-center justify-center ${checked ? "bg-[#E0218A]" : "bg-white"}`}>{checked && <Check size={16} className="text-white" strokeWidth={3} />}</span>
                  <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggle(a.key)} />
                  <span className="flex-1">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="font-bold text-[#121212]">{a.label}</span>
                      <span className="font-serif font-black text-[#121212] whitespace-nowrap">{a.price_label}</span>
                    </span>
                    <span className="block text-sm text-[#121212]/70 mt-1 leading-relaxed">{a.desc}</span>
                    {perGuest && checked && num > 0 && (
                      <span data-testid="favor-total" className="mt-2 inline-block text-sm font-bold text-[#E0218A]">{num} guests × $12 = ${favorTotal.toFixed(2)}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>

          {/* conditional notes */}
          {isSelected("matching_cake") && (
            <div className="mt-4"><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 block mb-1">Cake design notes</label>
              <textarea data-testid="fd-cake-notes" rows={2} value={cakeNotes} onChange={(e) => setCakeNotes(e.target.value)} placeholder="Colors, flavors, theme to match…" className="w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none resize-none" /></div>
          )}
          {isSelected("party_banner") && (
            <div className="mt-4"><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 block mb-1">Banner wording, colors, or design notes</label>
              <textarea data-testid="fd-banner-notes" rows={2} value={bannerNotes} onChange={(e) => setBannerNotes(e.target.value)} placeholder="e.g. 'Happy Birthday Mia!' in pink & gold" className="w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none resize-none" /></div>
          )}
        </div>

        {/* Dunkin' catering */}
        {data.dunkin_catalog?.length > 0 && (
          <div className="mt-8">
            <DunkinAddons catalog={data.dunkin_catalog} value={dunkin} onChange={setDunkin} />
          </div>
        )}

        {/* additional details */}
        <div className="mt-8 space-y-4">
          <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 block mb-1">Final theme or color notes</label>
            <input data-testid="fd-theme-notes" value={themeNotes} onChange={(e) => setThemeNotes(e.target.value)} className="w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none" /></div>
          <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 block mb-1">Allergy or dietary information</label>
            <input data-testid="fd-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} className="w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none" /></div>
          <div><label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/70 block mb-1">Special requests</label>
            <textarea data-testid="fd-special" rows={2} value={special} onChange={(e) => setSpecial(e.target.value)} className="w-full px-3 py-2 border-2 border-[#121212] bg-white focus:outline-none resize-none" /></div>
        </div>

        {/* confirm + submit */}
        <label data-testid="fd-confirm" className="mt-8 flex items-start gap-3 cursor-pointer bg-white border-2 border-[#121212] p-4">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="w-5 h-5 accent-[#E0218A] mt-0.5" />
          <span className="text-sm font-bold text-[#121212]">I confirm that all submitted party details are correct.</span>
        </label>
        <button data-testid="fd-submit" onClick={submit} disabled={busy} className="mt-5 w-full px-8 py-4 bg-[#E0218A] text-white hover:bg-[#c41879] disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all inline-flex items-center justify-center gap-3">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Submit final details"}
        </button>
        <p className="mt-3 text-xs text-center text-[#121212]/55">Nothing is charged here. After you submit, we'll email your final balance and a secure payment link.</p>
      </div>
    </div>
  );
}
